// components/Login.tsx
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Login: React.FC = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f5f5 0%, #e5e7eb 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: 'Inter, sans-serif'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '400px',
        background: 'rgba(255,255,255,0.28)',
        backdropFilter: 'blur(18px)',
        padding: '42px',
        borderRadius: '22px',
        boxShadow: '0 18px 35px rgba(0,0,0,0.15)',
        border: '1.5px solid rgba(255,255,255,0.4)'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 900,
          color: '#2563eb',
          textAlign: 'center',
          margin: '0 0 18px 0',
          fontFamily: "'Montserrat', 'Segoe UI', 'Roboto', Arial, sans-serif",
          letterSpacing: '2px'
        }}>
          Welcome Back
        </h1>

        <p style={{
          fontSize: '18px',
          color: '#111',
          margin: '0 0 30px 0',
          fontWeight: 600,
          textAlign: 'center',
          fontFamily: "'Montserrat', 'Segoe UI', 'Roboto', Arial, sans-serif"
        }}>
          Sign in to continue deploying
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '24px' }}>
          <div>
            <label style={{ fontWeight: 700, color: '#1f2937', marginBottom: '6px', display: 'block' }}>
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              style={{
                width: '100%',
                padding: '14px 16px',
                border: error ? '2px solid #f87171' : '1.6px solid #bfdbfe',
                borderRadius: '10px',
                fontSize: '16px',
                outline: 'none',
                background: 'rgba(255,255,255,0.85)',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <label style={{ fontWeight: 700, color: '#1f2937', marginBottom: '6px', display: 'block' }}>
              Password
            </label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{
                  width: '100%',
                  padding: '14px 50px 14px 16px',
                  border: error ? '2px solid #f87171' : '1.6px solid #bfdbfe',
                  borderRadius: '10px',
                  fontSize: '16px',
                  outline: 'none',
                  background: 'rgba(255,255,255,0.85)',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#64748b',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  {showPassword ? (
                    // Eye-off icon
                    <>
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </>
                  ) : (
                    // Eye icon
                    <>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: '14px 0',
              background: isLoading ? '#93c5fd' : '#1e40af',
              color: 'white',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '18px',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
            }}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        {error && (
          <div style={{
            marginTop: '16px',
            background: '#fee2e2',
            padding: '12px',
            borderRadius: '10px',
            color: '#b91c1c',
            fontWeight: 800,
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;